/**
 * Primary Lingerette — Wishlist
 * localStorage-based wishlist with Klaviyo + GA4 event tracking.
 * Phase 2: migrate storage to Shopify customer metafields.
 */

const Wishlist = (() => {
  const STORAGE_KEY = 'primary_wishlist';

  // ─── Storage helpers ─────────────────────────────────────────────────────

  function getAll() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  function has(productId) {
    return getAll().some(i => i.id === String(productId));
  }

  function add(product) {
    const items = getAll();
    if (items.some(i => i.id === String(product.id))) return;
    items.push({
      id:        String(product.id),
      variantId: String(product.variantId || ''),
      handle:    product.handle,
      title:     product.title,
      price:     product.price,       // in cents
      url:       product.url,
      image:     product.image,
      vendor:    product.vendor || '',
      addedAt:   new Date().toISOString(),
    });
    save(items);
    trackKlaviyo('Wishlist - Added Product', product);
    trackGA4('add_to_wishlist', product);
    updateKlaviyoProfile();
  }

  function remove(productId) {
    const items    = getAll();
    const item     = items.find(i => i.id === String(productId));
    const filtered = items.filter(i => i.id !== String(productId));
    save(filtered);
    if (item) {
      trackKlaviyo('Wishlist - Remove Product', item);
      trackGA4('remove_from_wishlist', item);
    }
    updateKlaviyoProfile();
  }

  function toggle(product) {
    has(product.id) ? remove(product.id) : add(product);
  }

  // ─── GA4 ─────────────────────────────────────────────────────────────────
  // Shopify Google app installs gtag() — use it directly.
  // Falls back silently if GA4 isn't present.

  function ga4Item(product) {
    const priceRaw = typeof product.price === 'number' ? product.price : parseInt(product.price || 0, 10);
    return {
      item_id:    String(product.id),
      item_name:  product.title,
      item_brand: product.vendor || '',
      price:      parseFloat((priceRaw / 100).toFixed(2)),
    };
  }

  function trackGA4(eventName, product) {
    if (typeof window.gtag !== 'function') return;
    const priceRaw = typeof product.price === 'number' ? product.price : parseInt(product.price || 0, 10);
    window.gtag('event', eventName, {
      currency: 'AUD',
      value:    parseFloat((priceRaw / 100).toFixed(2)),
      items:    [ga4Item(product)],
    });
  }

  function trackGA4WishlistView(items) {
    if (typeof window.gtag !== 'function' || !items.length) return;
    window.gtag('event', 'view_wishlist', {
      currency:     'AUD',
      wishlist_size: items.length,
      items:        items.map(ga4Item),
    });
  }

  // ─── Klaviyo ─────────────────────────────────────────────────────────────

  // ─── Klaviyo profile property sync ───────────────────────────────────────
  // Called after every add/remove. Sets two profile properties:
  //   wishlist_product_ids  — array of Shopify ProductID strings (for flows/segmentation)
  //   wishlist_products     — array of {ProductID, ProductName} objects (for readable UI)

  function updateKlaviyoProfile() {
    const items = getAll();
    const productIds = items.map(i => i.id);
    const productDetails = items.map(i => ({ ProductID: i.id, ProductName: i.title }));

    const doUpdate = () => {
      if (!window.klaviyo) return;
      window.klaviyo.identify({
        wishlist_product_ids: productIds,
        wishlist_products:    productDetails,
      });
    };

    if (window.klaviyo) {
      doUpdate();
    } else {
      window.addEventListener('klaviyo.initialized', doUpdate, { once: true });
      setTimeout(doUpdate, 2000);
    }
  }

  function trackKlaviyo(eventName, product) {
    const priceRaw = typeof product.price === 'number' ? product.price : parseInt(product.price || 0, 10);
    const payload = {
      ProductID:    String(product.id),
      VariantID:    String(product.variantId || ''),
      ProductName:  product.title,
      Handle:       product.handle,
      URL:          product.url,
      ImageURL:     product.image,
      Price:        (priceRaw / 100).toFixed(2),
      Vendor:       product.vendor || '',
      WishlistSize: getAll().length,
    };

    const fire = () => {
      if (window.klaviyo) window.klaviyo.track(eventName, payload);
    };

    if (window.klaviyo) {
      fire();
    } else {
      window.addEventListener('klaviyo.initialized', fire, { once: true });
      setTimeout(fire, 2000);
    }
  }

  function trackViewed() {
    const items = getAll();
    if (!items.length) return;

    // Klaviyo
    const klFire = () => {
      if (!window.klaviyo) return;
      window.klaviyo.track('Wishlist - Viewed Wishlist', {
        WishlistSize: items.length,
        Products: items.map(i => ({
          ProductID:   i.id,
          ProductName: i.title,
          Handle:      i.handle,
          URL:         i.url,
          Price:       ((parseInt(i.price, 10) || 0) / 100).toFixed(2),
        })),
      });
    };
    if (window.klaviyo) { klFire(); } else {
      window.addEventListener('klaviyo.initialized', klFire, { once: true });
    }

    // GA4
    trackGA4WishlistView(items);
  }

  // Call this from order confirmation page or purchase event
  function trackPurchasedFromWishlist(lineItems) {
    const wishlistIds = getAll().map(i => i.id);
    const matched = lineItems.filter(li => wishlistIds.includes(String(li.product_id)));
    matched.forEach(li => {
      if (window.klaviyo) {
        window.klaviyo.track('Purchased from Wishlist', {
          ProductID:   String(li.product_id),
          ProductName: li.title,
          Price:       li.price,
        });
      }
      // GA4: purchase_from_wishlist (custom event — tag in GA4 as conversion)
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase_from_wishlist', {
          currency: 'AUD',
          value:    parseFloat(li.price),
          items:    [{ item_id: String(li.product_id), item_name: li.title, price: parseFloat(li.price) }],
        });
      }
      remove(li.product_id);
    });
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────

  function updateButtonStates() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = btn.dataset.productId;
      const active = has(id);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-pressed', String(active));
      const label = btn.querySelector('.wishlist-btn__label');
      if (label) label.textContent = active ? 'Wishlisted' : 'Wishlist';
    });
  }

  function updateCount() {
    const count = getAll().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.classList.toggle('is-visible', count > 0);
    });
  }

  function refreshUI() {
    updateButtonStates();
    updateCount();
  }

  // ─── Wishlist page renderer ───────────────────────────────────────────────
  // Fetches real product data from Shopify's JSON API and renders using the
  // same product-card.liquid HTML structure as collection pages.

  const _productCache = {};
  let _wishlistPageViewed = false;

  const PLUS_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/></svg>`;
  const HEART_SVG = `<svg class="wishlist-btn__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>`;

  function formatMoney(cents) {
    const amount = parseInt(cents, 10) / 100;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
  }

  function escapeAttr(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function shopifyCdnSrc(src, width) {
    if (!src) return '';
    return src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
  }

  function buildProductCard(product) {
    const img1 = product.images[0];
    const img2 = product.images[1];
    const variant = product.variants[0];
    // Storefront JSON API returns prices in dollars (e.g. "75.00"), not cents
    const price = Math.round(parseFloat(variant.price) * 100);
    const compareAt = variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : null;
    const isOnSale = compareAt && compareAt > price;
    const img1Src = shopifyCdnSrc(img1 && img1.src, 800);
    const img2Src = shopifyCdnSrc(img2 && img2.src, 800);

    // Quick-add overlay (+ icon on card hover) — opens quick-buy modal for size selection
    const quickBuy = `
      <button type="button" aria-controls="wl-qb-${product.id}" class="product-card__quick-add-button">
        <span class="sr-only">Add to cart</span>
        ${PLUS_ICON}
      </button>
      <quick-buy-modal handle="${product.handle}?variant=${variant.id}" class="quick-buy-modal modal" id="wl-qb-${product.id}">
      </quick-buy-modal>`;

    // Wishlist button (already wishlisted — heart is filled)
    const wlBtn = `
      <button type="button"
        class="wishlist-btn wishlist-btn--card is-wishlisted"
        data-wishlist-btn
        data-product-id="${product.id}"
        data-product-variant-id="${variant.id}"
        data-product-handle="${escapeAttr(product.handle)}"
        data-product-title="${escapeAttr(product.title)}"
        data-product-price="${price}"

        data-product-url="/products/${product.handle}"
        data-product-image="${img1Src}"
        data-product-vendor="${escapeAttr(product.vendor || '')}"
        aria-pressed="true"
        aria-label="Remove ${escapeAttr(product.title)} from wishlist"
      >${HEART_SVG}</button>`;

    // Price — match price-list.liquid card context with product_card_text_font=heading
    const priceHtml = `
      <price-list class="price-list">
        <sale-price class="h6 ${isOnSale ? 'text-on-sale' : 'text-subdued'}">
          <span class="sr-only">${isOnSale ? 'Sale price' : 'Price'}</span>
          ${formatMoney(price)}
        </sale-price>
        ${isOnSale ? `<compare-at-price class="h6 text-subdued line-through"><span class="sr-only">Regular price</span>${formatMoney(compareAt)}</compare-at-price>` : ''}
      </price-list>`;

    // Add to cart button — opens quick-buy modal for variant/size selection
    const cartBtn = `<button type="button" aria-controls="wl-qb-${product.id}" class="button button--full-width">Add to cart</button>`;

    return `
      <product-card class="product-card" handle="${product.handle}">
        <div class="product-card__figure">
          <a href="/products/${product.handle}" class="product-card__media" draggable="false">
            ${img1Src ? `<img src="${img1Src}" alt="${escapeAttr(product.title)}" class="product-card__image product-card__image--primary" loading="lazy">` : ''}
            ${img2Src ? `<img src="${img2Src}" alt="" class="product-card__image product-card__image--secondary" loading="lazy" aria-hidden="true">` : ''}
          </a>
          ${quickBuy}
          ${wlBtn}
        </div>
        <div class="product-card__info">
          <div class="v-stack justify-items-center gap-2">
            <div class="v-stack justify-items-center gap-1">
              <a href="/products/${product.handle}" class="product-title h6">${product.title}</a>
              ${priceHtml}
            </div>
            ${cartBtn}
          </div>
        </div>
      </product-card>`;
  }

  function emptyState() {
    return `<div class="wishlist-empty">
      <p>Your wishlist is empty.</p>
      <a href="/collections/all" class="button">Start browsing</a>
    </div>`;
  }

  async function renderWishlistPage() {
    const container = document.getElementById('wishlist-items');
    if (!container) return;

    const items = getAll();

    if (!items.length) {
      container.innerHTML = emptyState();
      return;
    }

    // Fetch products not yet cached
    const toFetch = items.filter(i => !_productCache[i.handle]);
    if (toFetch.length) {
      container.innerHTML = '<div style="padding:4rem;text-align:center;grid-column:1/-1">Loading your wishlist…</div>';
      await Promise.all(toFetch.map(item =>
        fetch(`/products/${encodeURIComponent(item.handle)}.json`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d && d.product) _productCache[item.handle] = d.product; })
          .catch(() => null)
      ));
    }

    // Render only currently-wishlisted products (handles removals)
    const cards = items
      .map(item => _productCache[item.handle] ? buildProductCard(_productCache[item.handle]) : null)
      .filter(Boolean);

    if (!cards.length) {
      container.innerHTML = emptyState();
      return;
    }

    container.innerHTML = cards.join('');
    updateButtonStates();

    if (!_wishlistPageViewed) {
      trackViewed();
      _wishlistPageViewed = true;
    }
  }

  // ─── Cart add detection ───────────────────────────────────────────────────
  // Intercept Shopify fetch-based cart adds and fire if product is wishlisted.

  function trackCartAdd(wishlistItem) {
    const priceRaw = typeof wishlistItem.price === 'number' ? wishlistItem.price : parseInt(wishlistItem.price || 0, 10);
    const payload = {
      ProductID:   wishlistItem.id,
      VariantID:   wishlistItem.variantId || '',
      ProductName: wishlistItem.title,
      Handle:      wishlistItem.handle,
      URL:         wishlistItem.url,
      Price:       (priceRaw / 100).toFixed(2),
      Vendor:      wishlistItem.vendor || '',
    };
    const fire = () => {
      if (window.klaviyo) window.klaviyo.track('Wishlist - Added to Cart', payload);
    };
    if (window.klaviyo) { fire(); } else {
      window.addEventListener('klaviyo.initialized', fire, { once: true });
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'wishlist_add_to_cart', {
        currency: 'AUD',
        value:    parseFloat((priceRaw / 100).toFixed(2)),
        items:    [ga4Item(wishlistItem)],
      });
    }
  }

  function watchCartAdds() {
    const origFetch = window.fetch;
    window.fetch = async function(...args) {
      const url = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';
      const response = await origFetch.apply(this, args);
      if (!url.includes('/cart/add')) return response;

      try {
        const clone = response.clone();
        const data  = await clone.json();
        const items = data.items || (data.variant_id ? [data] : []);
        items.forEach(item => {
          const vid = String(item.variant_id || '');
          const pid = String(item.product_id || '');
          const match = getAll().find(w => w.id === pid || w.variantId === vid);
          if (match) trackCartAdd(match);
        });
      } catch { /* non-JSON or error — ignore */ }

      return response;
    };
  }

  // ─── Button click handler ─────────────────────────────────────────────────

  function handleButtonClick(e) {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();

    const product = {
      id:        btn.dataset.productId,
      variantId: btn.dataset.productVariantId || '',
      handle:    btn.dataset.productHandle,
      title:     btn.dataset.productTitle,
      price:     btn.dataset.productPrice,
      url:       btn.dataset.productUrl,
      image:     btn.dataset.productImage,
      vendor:    btn.dataset.productVendor || '',
    };

    toggle(product);
    refreshUI();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    document.addEventListener('click', handleButtonClick);
    window.addEventListener('wishlist:updated', () => {
      refreshUI();
      if (document.getElementById('wishlist-items')) renderWishlistPage();
    });
    watchCartAdds();
    refreshUI();

    if (document.getElementById('wishlist-items')) {
      renderWishlistPage();
    }
  }

  // Public API
  return { init, has, add, remove, toggle, getAll, trackPurchasedFromWishlist, refreshUI };
})();

// Guard against timing issues — run immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', Wishlist.init);
} else {
  Wishlist.init();
}
